import React, { useState } from 'react';

import {
  SEGMENT_LABEL,
  segmentColor,
  segmentTextColor,
} from '../trees/nodeSegment';
import { overrideRuleSentence } from './overrideSentence';
import { signedPoints } from '../../../models/pointBasedForm/pointsRange';
import {
  ScoreCardData,
  ScoreCardSignal,
  ScoreCardTree,
  asThreshold,
  treeLabel,
  weightText,
} from './scoreCardData';
import './scoreCard.css';

/**
 * One record scored against this model, drawn from the lookup's own receipt.
 *
 * A score is a chain and the chain is the answer: the trees blend to a result, an
 * override may cap it, and a set of flags lands on the record for a rep to read. Written
 * out that chain is four paragraphs, which is what this replaces — the reader was holding
 * the structure in their head rather than reading it off the screen.
 *
 * Open by default, unlike a split card. A build lands eleven splits and one lookup, so
 * there is no list to keep short, and a folded card would hide the thing just asked for.
 *
 * Nothing here is composed from free text: the override goes through
 * `overrideRuleSentence` (the overrides page's own renderer), a signal's label is the
 * sentence Studio built for the lead sample, and the segments are the four bands the
 * whole fit surface speaks, drawn in the tree page's palette. Nothing on this card can
 * drift from the page it sits beside.
 *
 * A tree row says WHAT that tree made of the lead and how much of the blend it is, and
 * stops there. The path it took is a click away on the leaf the row links to, and the
 * score lookup page draws it properly; a walk redrawn in a chat bubble was that page
 * again, worse.
 */
export function ScoreCard({
  card,
  onFocus,
}: {
  card: ScoreCardData;
  onFocus: (targetType: string, targetId: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const segment = asThreshold(card.segment);
  const before = asThreshold(card.override?.segment_before);
  // Only when the rule actually MOVED it. A rule that fired on a lead already in that
  // band changed nothing, and drawing an arrow from Good to Good says it did.
  const moved = Boolean(card.override?.moved_segment) && before !== null;
  const signals = card.signals ?? [];
  const rules = card.rules ?? [];
  // A rule based card names rules where a tree card names trees, including in the
  // override line below: "the segment the trees had already reached" is untrue of it.
  const scoredBy = card.base && card.base !== 'trees' ? 'rules' : 'trees';

  return (
    <section className="mscore">
      <header className="mscore-hd">
        <span className="mscore-who" title={card.email}>
          {card.email}
        </span>
        <span className="mscore-out">
          {moved && (
            <>
              <Chip segment={before} muted />
              <span className="mscore-arrow" aria-label="overridden to">
                →
              </span>
            </>
          )}
          <Chip segment={segment} score={card.score} />
        </span>
      </header>

      {card.override && (
        <p className="mscore-rule">
          <b>{moved ? 'An override moved this.' : 'An override matched.'}</b>{' '}
          {card.override.rule ? (
            <>{overrideRuleSentence(card.override.rule)}</>
          ) : (
            <>
              Rule{' '}
              {typeof card.override.rule_index === 'number'
                ? card.override.rule_index + 1
                : '?'}{' '}
              decided the segment.
            </>
          )}
          {!moved && ` It set the segment the ${scoredBy} had already reached.`}
        </p>
      )}

      {card.trees.length > 0 && (
        <div className="mscore-scroll">
          <table className="mscore-table">
            <thead>
              <tr>
                <th>Tree</th>
                <th>Said</th>
                <th className="mscore-r">Of the score</th>
              </tr>
            </thead>
            <tbody>
              {card.trees.map((tree) => (
                <TreeRow
                  key={tree.tree}
                  tree={tree}
                  onFocus={onFocus}
                  single={card.trees.length === 1}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rules.length > 0 && (
        <div className="mscore-scroll">
          <table className="mscore-table" data-testid="score-card-rules">
            <thead>
              <tr>
                <th>Because</th>
                <th className="mscore-r">Points</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  <th scope="row">{rule.because || 'A rule matched'}</th>
                  <td className="mscore-r">{signedPoints(rule.points)}</td>
                </tr>
              ))}
            </tbody>
            {typeof card.rules_total === 'number' && (
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  <td className="mscore-r">
                    <b>{card.rules_total}</b>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {Boolean(card.unused_trees) && (
        <p className="mscore-note">
          {card.unused_trees} more {card.unused_trees === 1 ? 'tree' : 'trees'}{' '}
          built but carrying no weight, so {card.unused_trees === 1 ? 'it' : 'they'}{' '}
          did not affect this score.
        </p>
      )}

      {signals.length > 0 && (
        <div className="mscore-sigs">
          <button
            type="button"
            className="mscore-sigs-hd"
            aria-expanded={open}
            onClick={() => setOpen((was) => !was)}
          >
            <span>
              What a rep would see
              <em> · {signals.length}</em>
            </span>
            <span className={`mscore-chev${open ? ' mscore-chev--up' : ''}`} />
          </button>
          {card.signals_disagree && (
            <p className="mscore-warn">
              These read the opposite way to the score, so the record gives a rep
              no reason for it.
            </p>
          )}
          {open && (
            <ul className="mscore-flags">
              {signals.map((signal, i) => (
                <Flag key={`${signal.group}-${i}`} signal={signal} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

/** The segment as the rest of Studio draws it: the bar chart's own fill and label. */
function Chip({
  segment,
  score,
  muted,
}: {
  segment: ReturnType<typeof asThreshold>;
  score?: number | null;
  muted?: boolean;
}) {
  const background = segmentColor(segment);
  // No fill rather than a guessed one. A chip with a transparent background still
  // reads as a chip; one painted an undefined colour reads as broken.
  const style = background
    ? { background, color: segmentTextColor(segment) ?? undefined }
    : undefined;
  return (
    <span
      className={`mscore-chip${muted ? ' mscore-chip--was' : ''}`}
      style={style}
    >
      {segment ? SEGMENT_LABEL[segment] : 'No segment'}
      {typeof score === 'number' && <b>{Math.round(score)}</b>}
    </span>
  );
}

function TreeRow({
  tree,
  single,
  onFocus,
}: {
  tree: ScoreCardTree;
  /** A one-tree model needs no weight: it is 100% of itself. */
  single: boolean;
  onFocus: (targetType: string, targetId: string) => void;
}) {
  const segment = asThreshold(tree.segment);
  const weight = single ? null : weightText(tree.weight);

  return (
    <tr>
      <th scope="row">
        <button
          type="button"
          className="mscore-node"
          onClick={() =>
            tree.leaf != null && onFocus('tree_node', `${tree.tree}:${tree.leaf}`)
          }
          disabled={tree.leaf == null}
          title={tree.leaf != null ? 'Open the leaf it landed in' : undefined}
        >
          {treeLabel(tree.name, tree.tree)}
        </button>
      </th>
      <td>
        <Chip segment={segment} />
      </td>
      <td className="mscore-r">{weight ?? (single ? 'all of it' : '—')}</td>
    </tr>
  );
}

function Flag({ signal }: { signal: ScoreCardSignal }) {
  return (
    <li
      className={`mscore-flag mscore-flag--${signal.argues_for ? 'for' : 'against'}`}
    >
      <span aria-hidden="true">{signal.argues_for ? '+' : '−'}</span>
      {signal.label}
    </li>
  );
}
